import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Typography,
  Autocomplete,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useBranchData } from 'src/context/providers/BranchContext';
import { useOrderActions } from 'src/context/providers/OrderContext';
import { toast } from 'react-toastify';
import LimitWarnings, { confirmedCodesFor } from './limit-warnings';

const PAID_US_DELIVERIES = ['US_Region'];

// ===== Helpers =====
const DELIVERY_PREFIX = {
  Tbilisi_Pickup: 'PR',
  Tbilisi_Delivery: 'DR',
  Region: 'RR',
  NY_Pickup: 'NP',
  NY_Delivery: 'ND',
  US_Region: 'UR',
};
const ORDER_CATEGORIES = [
  'Household goods',
  'Clothing',
  'Phone',
  'Computer',
  'Jewelry',
  'Toys',
  'Auto/Moto parts',
  'Food supplements',
  'Audio/Video equipment',
  'Cosmetics',
  'Parfumes',
  'Glass products',
  'Printed products',
  'Lamps',
  'Glasses (Sunglasses)',
  'Footwear',
  'Bags',
  'Sports Equipment',
  'Tools',
  'Various consumer products',
  'Computer parts',
  'Music Instruments',
  'Painting',
];

function formatYYMMDDHHmmss(date = new Date()) {
  const y = String(date.getFullYear()).slice(-2);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
}

// Generate one unique tracking for this session
function generateOneTracking({ deliveryType, existing = new Set() }) {
  const prefix = DELIVERY_PREFIX[deliveryType] || 'PR';
  const base = `${prefix}${formatYYMMDDHHmmss()}`;
  if (!existing.has(base)) return base;

  let idx = 1;
  let candidate = `${base}${String(idx).padStart(2, '0')}`;
  while (existing.has(candidate)) {
    idx += 1;
    candidate = `${base}${String(idx).padStart(2, '0')}`;
  }
  return candidate;
}

// (W * H * L) / 6000, cm -> kg
function volumetricWeightKg({ width, height, len }) {
  if (!width || !height || !len) return null;
  const vol = (Number(width) * Number(height) * Number(len)) / 6000;
  return Number.isFinite(vol) ? Number(vol.toFixed(2)) : null;
}

function clampMinWeight200g(kg) {
  if (kg == null) return null;
  return Math.max(0.2, Number(kg));
}

function receiverLabel(option) {
  if (!option) return '';
  const first = option.first_name || option.name || '';
  const last = option.last_name || option.lname || '';
  const phone = option.phone ? ` • ${option.phone}` : '';
  return `${first} ${last}${phone}`.trim();
}

// ===== Component =====
const ParcelGenerationModal = ({
  open,
  onClose,
  deliveryType,
  receiverOptions,
  onAttach,
  aRow,
}) => {
  const [parcels, setParcels] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null); // { type: 'submit', count } | { type: 'close' } | null
  const { createPhysicalOrder, checkPhysicalLimits } = useOrderActions();

  const actualClose = () => {
    setParcels([]);
    setConfirmOpen(null);
    onClose();
  };

  const { filteredReceiverOptions, primaryFirst } = useMemo(() => {
    const usedIds = (aRow?.orders || []).map((o) => o.receiver.id);

    return {
      filteredReceiverOptions: receiverOptions.filter((r) => !usedIds.includes(r.id)),
      primaryFirst: !aRow.orders.length
        ? receiverOptions.filter((r) => r.id === aRow?.primaryReceiverId)[0]
        : null,
    };
  }, [aRow?.orders, aRow?.primaryReceiverId, receiverOptions]);

  useEffect(() => {
    if (!open || !deliveryType) return;
    if (parcels.length === 0) {
      const tracking = generateOneTracking({ deliveryType, existing: new Set() });

      setParcels([
        {
          tracking,
          weight: null,
          width: null,
          height: null,
          len: null,
          volume: null,
          declarationContent: ORDER_CATEGORIES[0],
          declarationValue: aRow.orders[0]?.declarationValue
            ? aRow?.orders[0]?.declarationValue
            : 60,
          group_id: aRow.orders[0]?.group_id ? aRow?.orders[0]?.group_id : null,
          additional_identifier: '',
          receiver: primaryFirst,
          price: null,
          quantity: 1,
          delivery_fee_usd: null,
        },
      ]);
    }
  }, [open, deliveryType, aRow, parcels.length, filteredReceiverOptions, primaryFirst]);
  const handleCloseRequest = () => {
    if (submitting) return;
    setConfirmOpen({ type: 'close' });
  };

  const addParcel = () => {
    const existing = new Set(parcels.map((p) => p.tracking));
    const tracking = generateOneTracking({ deliveryType, existing });
    const defaultDeclarationValue = aRow?.orders?.[0]?.declarationValue ?? 60;
    const defaultGroupId = aRow?.orders?.[0]?.group_id ?? null;
    setParcels((prev) => [
      ...prev,
      {
        tracking,
        weight: null,
        width: null,
        height: null,
        len: null,
        volume: null,
        declarationContent: ORDER_CATEGORIES[0],
        declarationValue: defaultDeclarationValue,
        group_id: defaultGroupId,
        additional_identifier: '',
        receiver: null,
        price: null,
        quantity: 1,
        delivery_fee_usd: null,
      },
    ]);
  };

  const removeParcel = (idx) => {
    setParcels((prev) => prev.filter((_, i) => i !== idx));
  };

  // Immutable helper to update one parcel and recompute volumetric weight when dims change
  const updateParcel = (idx, patch) => {
    setParcels((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row, ...patch };

        // recompute volumetric if any dimension changed
        if (
          Object.prototype.hasOwnProperty.call(patch, 'width') ||
          Object.prototype.hasOwnProperty.call(patch, 'height') ||
          Object.prototype.hasOwnProperty.call(patch, 'len')
        ) {
          const vol = volumetricWeightKg({
            width: next.width,
            height: next.height,
            len: next.len,
          });
          next.volume = vol != null ? clampMinWeight200g(vol) : null;
        }
        return next;
      })
    );
  };
  const branch = useBranchData();

  const buildCreateData = (parcel, groupId) => ({
    tracking_number: parcel.tracking,
    primaryReceiverId: aRow.primaryReceiverId,
    status: aRow.status,
    customer_id: aRow.customer_id,
    destination_delivery: aRow.delivery_type,
    pallet_id: aRow.pallet_id,
    secondaryReceiverId: parcel.receiver.id,
    weight: parcel.weight,
    width: parcel.width,
    height: parcel.height,
    len: parcel.len,
    volume: parcel.volume,
    category: parcel.declarationContent,
    declarationContent: parcel.declarationContent,
    declarationValue: parcel?.declarationValue,
    additional_identifier: parcel.additional_identifier,
    price: parcel.price,
    quantity: parcel.quantity ?? 1,
    delivery_fee_usd: PAID_US_DELIVERIES.includes(aRow.delivery_type)
      ? parcel.delivery_fee_usd
      : null,
    declaration_status: 'DECLERED',
    branch_id: branch.filter((data) => data.name === 'USA')[0].id,
    group_id: groupId ?? (aRow.group_id || null),
    pickup_area: aRow.pickup_area || null,
    pickup_price: aRow.pickup_price ?? null,
    promotion: aRow.promotion ?? null,
  });

  const handleSubmit = async () => {
    if (parcels.length === 0) return;
    if (typeof onAttach !== 'function') return;

    const parcelsWithReceiver = parcels.filter((p) => p.receiver);
    if (parcelsWithReceiver.length === 0) {
      toast.error('At least one parcel must have a Receiver.');
      return;
    }
    // Prevent assigning the same receiver to more than one parcel
    const receiverIds = parcelsWithReceiver.map((p) => p.receiver?.id).filter(Boolean);
    const uniqueReceiverIds = new Set(receiverIds);
    if (receiverIds.length !== uniqueReceiverIds.size) {
      toast.error('Each parcel must have a different Receiver.');
      return;
    }
    if (parcelsWithReceiver.some((p) => p.weight == null || p.weight < 0.2)) {
      toast.error('Each parcel must have Actual Weight min 0.2');
      return;
    }
    if (parcelsWithReceiver.some((p) => !p.declarationContent || p.declarationContent.length === 0)) {
      toast.error('Each parcel must have Actual Content');
      return;
    }
    if (parcelsWithReceiver.some((p) => p?.declarationValue == null)) {
      toast.error('Each parcel must have Actual Content value.');
      return;
    }

    const groupId = aRow.group_id || null;
    const warnings = await checkPhysicalLimits(
      parcelsWithReceiver.map((p) => ({
        tracking_number: p.tracking,
        receiver_id: p.receiver.id,
        pallet_id: aRow.pallet_id,
        weight: p.weight,
        declared_value_usd: p.declarationValue,
      })),
      aRow.direction
    );
    const payload = parcelsWithReceiver.map((p) => ({
      ...buildCreateData(p, groupId),
      limit_warnings_confirmed: confirmedCodesFor(warnings, {
        tracking: p.tracking,
        receiverId: p.receiver.id,
      }),
    }));
    setConfirmOpen({
      type: 'submit',
      count: parcelsWithReceiver.length,
      payload,
      parcelsWithReceiver,
      groupId,
      warnings,
    });
  };

  const handleConfirmConfirm = async () => {
    if (!confirmOpen) return;
    if (confirmOpen.type === 'close') {
      actualClose();
      return;
    }
    if (confirmOpen.type === 'submit') {
      const { payload, parcelsWithReceiver, groupId } = confirmOpen;
      setConfirmOpen(null);
      setSubmitting(true);
      try {
        const data = await createPhysicalOrder(payload, aRow.direction, {
          delivery_area_id: aRow.delivery_area_id,
        });
        if (!data) return;
        const resolvedGroupId = data.group_id ?? data[0]?.group_id ?? groupId;
        onAttach(parcelsWithReceiver, resolvedGroupId);
        setParcels([]);
        onClose();
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(null);
  };

  // ---- No nested ternary; use a small renderer ----
  const renderContent = () => {
    if (!deliveryType) {
      return (
        <Typography color="text.secondary">
          Select a Delivery Type first, then open this modal.
        </Typography>
      );
    }

    if (parcels.length === 0) {
      return <Typography color="text.secondary">Preparing parcel…</Typography>;
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        {parcels.map((p, idx) => (
          <Card key={p.tracking}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Parcel {idx + 1} – Tracking: <strong>{p.tracking}</strong>
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeParcel(idx)}
                  disabled={submitting || parcels.length <= 1 || idx === 0}
                  aria-label="Remove parcel"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                <Box sx={{ gridColumn: 'span 6' }}>
                  <Autocomplete
                    options={filteredReceiverOptions || []}
                    value={p.receiver}
                    isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                    getOptionLabel={receiverLabel}
                    getOptionDisabled={(opt) =>
                      parcels.some(
                        (other, otherIdx) =>
                          otherIdx !== idx && (other.receiver?.id ?? '') === (opt?.id ?? '')
                      )
                    }
                    onChange={(e, val) => updateParcel(idx, { receiver: val })}
                    disabled={idx === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={idx === 0 ? 'Receiver (main)' : 'Receiver'}
                        variant="filled"
                      />
                    )}
                  />
                </Box>

                {/* Content */}
                <Box sx={{ gridColumn: 'span 4' }}>
                  <Autocomplete
                    options={ORDER_CATEGORIES}
                    value={p.declarationContent}
                    onChange={(e, newValue) =>
                      updateParcel(idx, {
                        declarationContent: newValue ?? ORDER_CATEGORIES[0],
                      })
                    }
                    renderInput={(params) => <TextField {...params} />}
                    fullWidth
                  />
                </Box>

                {/* Value */}
                <Box sx={{ gridColumn: 'span 2' }}>
                  <TextField
                    type="number"
                    label="Value (price)"
                    variant="filled"
                    value={p?.declarationValue ?? ''}
                    onChange={(e) =>
                      updateParcel(idx, {
                        declarationValue: Number(e.target.value) || null,
                      })
                    }
                    fullWidth
                  />
                </Box>

                {/* Actual weight */}
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Weight"
                    variant="filled"
                    value={p.weight ?? ''}
                    onChange={(e) =>
                      updateParcel(idx, {
                        weight: e.target.value,
                      })
                    }
                    helperText="Min 0.2 kg"
                    fullWidth
                  />
                </Box>

                {/* Dimensions (cm) */}
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Width"
                    variant="filled"
                    value={p.width ?? ''}
                    onChange={(e) => updateParcel(idx, { width: Number(e.target.value) || null })}
                    fullWidth
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Height"
                    variant="filled"
                    value={p.height ?? ''}
                    onChange={(e) => updateParcel(idx, { height: Number(e.target.value) || null })}
                    fullWidth
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Length"
                    variant="filled"
                    value={p.len ?? ''}
                    onChange={(e) => updateParcel(idx, { len: Number(e.target.value) || null })}
                    fullWidth
                  />
                </Box>

                {/* Volumetric (readonly) */}
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    label="Volume"
                    variant="filled"
                    value={p.volume ?? ''}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Box>

                {/* Additional cost */}
                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Additional Cost"
                    variant="filled"
                    value={p.price ?? ''}
                    onChange={(e) => updateParcel(idx, { price: Number(e.target.value) || null })}
                    fullWidth
                  />
                </Box>

                <Box sx={{ gridColumn: 'span 3' }}>
                  <TextField
                    type="number"
                    label="Quantity"
                    variant="filled"
                    inputProps={{ min: 1, step: 1 }}
                    value={p.quantity ?? 1}
                    onChange={(e) =>
                      updateParcel(idx, {
                        quantity: Math.max(1, parseInt(e.target.value, 10) || 1),
                      })
                    }
                    fullWidth
                  />
                </Box>

                {PAID_US_DELIVERIES.includes(deliveryType) && (
                  <Box sx={{ gridColumn: 'span 3' }}>
                    <TextField
                      type="number"
                      label="US delivery fee (USD)"
                      variant="filled"
                      inputProps={{ min: 0 }}
                      value={p.delivery_fee_usd ?? ''}
                      onChange={(e) =>
                        updateParcel(idx, {
                          delivery_fee_usd: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      fullWidth
                    />
                  </Box>
                )}

                {/* additional_identifier */}
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    label="Note"
                    variant="filled"
                    value={p.additional_identifier}
                    onChange={(e) => updateParcel(idx, { additional_identifier: e.target.value })}
                    fullWidth
                    multiline
                    maxRows={3}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={addParcel}
          disabled={submitting}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add parcel
        </Button>
      </Box>
    );
  };

  let confirmMessage = '';
  if (confirmOpen?.type === 'submit') {
    confirmMessage = `Are you sure you want to register ${confirmOpen.count} order(s)?`;
  } else if (confirmOpen?.type === 'close') {
    confirmMessage = 'Are you sure you want to cancel order registration?';
  }

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth onClose={handleCloseRequest}>
        <DialogTitle>Generate Parcels</DialogTitle>
        <DialogContent dividers>{renderContent()}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequest} disabled={submitting}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={!parcels.length || submitting}
            onClick={handleSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Creating orders…' : 'Create orders'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmOpen)}
        onClose={handleConfirmCancel}
        maxWidth={confirmOpen?.warnings?.length ? 'sm' : 'xs'}
        fullWidth
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <LimitWarnings warnings={confirmOpen?.warnings} />
          <Typography>{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel}>No</Button>
          <Button variant="contained" onClick={handleConfirmConfirm} color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ParcelGenerationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  deliveryType: PropTypes.oneOf([
    'Tbilisi_Pickup',
    'Tbilisi_Delivery',
    'Region',
    'NY_Pickup',
    'NY_Delivery',
    'US_Region',
  ]),
  receiverOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      first_name: PropTypes.string,
      name: PropTypes.string,
      last_name: PropTypes.string,
      lname: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  onAttach: PropTypes.func.isRequired,
  aRow: PropTypes.object,
};

ParcelGenerationModal.defaultProps = {
  deliveryType: undefined,
  receiverOptions: [],
};

export default ParcelGenerationModal;
